import type { NativeIntakeField } from '@ohf/core/shared';
import copy from '../copy/en/intake.json' with { type: 'json' };

// Mirror the existing native server contract for immediate feedback. Parity
// tests exercise the real server validator; server validation remains mandatory.
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const states = new Set('AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC'.split(' '));
const text = (value: unknown): string => typeof value === 'string' ? value.trim() : '';

export function nativeFieldError(field: NativeIntakeField, value: unknown): string | undefined {
  if (field.kind === 'multi') {
    if (value === undefined || Array.isArray(value) && !value.length) return field.required ? copy.chooseAtLeastOneOption : undefined;
    if (!Array.isArray(value) || !value.every(item => typeof item === 'string' && field.options.includes(item)) || new Set(value).size !== value.length) return copy.validationChooseFromList;
    if (value.length > 1 && value.some(item => field.exclusiveOptions?.includes(item))) return copy.validationScannerStatus;
    return;
  }
  const answer = text(value);
  if (field.key === 'business.npi' && !/^[0-9]{10}$/.test(answer)) return copy.npiTenDigits;
  if (!answer) return field.required ? copy.validationRequired : undefined;
  if (answer.length > 4000 || answer.includes('\0')) return copy.validationText;
  if (field.options.length && !field.options.includes(answer)) return copy.validationChooseFromList;
  if (field.kind === 'email' && !emailPattern.test(answer)) return copy.validationEmail;
  if (field.kind === 'tel' && answer.replace(/\D/g, '').length < 10) return copy.validationPhone;
  if (field.kind === 'number') {
    if (!/^\d+$/.test(answer)) return copy.countWholeNumber;
    if (field.numberConstraints) {
      const value = Number(answer), { min, max } = field.numberConstraints;
      if (!Number.isSafeInteger(value) || value > max) return copy.countTooLarge;
      if (value < min) return copy.countWholeNumber;
    }
  }
  if (field.key === 'locations.countryCode' && !['US', 'USA', 'United States', 'United States of America'].includes(answer)) return copy.thisAgreementSupportsUnitedStatesPractices;
}

/** Identity names match the submitted native payload and its rendered controls. */
export function nativeIdentityErrors(data: Record<string, unknown>): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = text(data.signerName);
  if (name.length < 2) errors.signerName = copy.validationSignerName;
  else if (name.length > 120) errors.signerName = copy.validationSignerNameLength;
  if (text(data.practiceName).length < 2) errors.practiceName = copy.validationPracticeName;
  if (text(data.legalCompanyName).length < 2) errors['intakeProfile.business.legalName'] = copy.validationLegalName;
  if (!emailPattern.test(text(data.email))) errors.email = copy.validationEmail;
  if (text(data.phone).replace(/\D/g, '').length < 10) errors['intakeProfile.people.officePhone'] = copy.validationPhone;
  if (text(data.addressStreet).length < 3) errors['intakeProfile.locations.addressLine1'] = copy.validationStreet;
  if (text(data.city).length < 2) errors['intakeProfile.locations.city'] = copy.validationCity;
  if (!states.has(text(data.state).toUpperCase())) errors['intakeProfile.locations.region'] = copy.validationState;
  if (!/^\d{5}(-\d{4})?$/.test(text(data.zip))) errors['intakeProfile.locations.postalCode'] = copy.validationZip;
  if (!text(data.signerTitle)) errors.signerTitle = copy.validationSignerTitle;
  return errors;
}
